import { useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { api, errorMessage } from "../services/api";
import SectionCard from "./SectionCard";
import { parseAi } from "./ContractsDialog";

const ALL_EXAMPLES = [
  "Which vendors may use our data to train their AI models?",
  "Which contracts do not promise breach notice within 72 hours?",
  "Which contracts renew automatically, and how much notice do we need to give?",
  "Is any of our data stored outside the United States?",
];
export const ONE_EXAMPLES = [
  "Can the vendor train its AI on our data?",
  "How quickly must the vendor tell us about a breach?",
  "What happens to our data when the contract ends?",
  "How do we cancel, and how much notice is needed?",
];

/**
 * A question box with the answer and its quoted clauses.
 * scope: {} asks every contract in the department, { vendorId } one vendor's, { contractId } a single document.
 */
export function AskBox({ scope = {}, examples = ALL_EXAMPLES, placeholder }) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState(null); // { answer, sources, searched, truncated }

  async function ask(text) {
    const q = (text ?? question).trim();
    if (!q || busy) return;
    setQuestion(q);
    setBusy(true);
    setError(null);
    try {
      const r = await api.askContracts(q, scope);
      const parsed = parseAi(r.resultJson);
      setReply({
        answer: parsed && typeof parsed.answer === "string" ? parsed.answer : "The AI's answer could not be read. Try asking again.",
        sources: parsed && Array.isArray(parsed.sources) ? parsed.sources : [],
        searched: r.contractsSearched,
        truncated: r.truncated,
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        <TextField fullWidth size="small" value={question} placeholder={placeholder || "For example: which vendors may train on our data?"}
          inputProps={{ maxLength: 600, "aria-label": "Question about your contracts" }}
          onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} />
        <Button variant="contained" disabled={busy || !question.trim()} onClick={() => ask()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRoundedIcon />} sx={{ whiteSpace: "nowrap" }}>
          {busy ? "Reading…" : "Ask"}
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
        {examples.map((e) => <Chip key={e} size="small" variant="outlined" label={e} onClick={() => ask(e)} disabled={busy} />)}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {reply && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ whiteSpace: "pre-line" }}>{reply.answer}</Typography>
          {reply.sources.length > 0 && (
            <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
              <Typography variant="overline" color="text.secondary">Where it says so</Typography>
              {reply.sources.map((s, i) => (
                <Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 1.5, borderLeft: "3px solid", borderColor: "divider" }}>
                  <b>{s.vendor}</b>: <i>“{s.quote}”</i>
                </Typography>
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            AI-generated from {reply.searched} {reply.searched === 1 ? "contract" : "contracts"}
            {reply.truncated ? " (long documents were only partly read)" : ""}. Check the quoted clauses before relying on this.
          </Typography>
        </Box>
      )}
    </>
  );
}

/** Ask a question across every uploaded contract in the current department. */
export default function AskContractsCard() {
  return (
    <SectionCard title="Ask your contracts" subtitle="Plain-language questions answered from the contracts uploaded for this department, with the clause quoted.">
      <AskBox />
    </SectionCard>
  );
}
